import dayjs from 'dayjs';
import * as yup from 'yup';

import {
  DEFAULT_SERVICE_TYPE_ID,
  OVERNIGHT_SERVICE_TYPE_IDS,
  SERVICE_TYPES_SUPPORTING_RWB,
  SERVICE_TYPE_SLUGS_TO_SERVICE_TYPE_IDS,
} from '~/common/constants/app';
import { chronologyValidationObject, petTypesValidationObject } from '~/common/utils/validation';
import {
  BOOKING_TYPE_MARKETPLACE,
  BOOKING_TYPE_RWB,
  getPetTypesFromSearchFilters,
} from '~/components/utils/search';
import { SitterApi } from '~/services/sitter/SitterApi';

import type { AxiosError } from 'axios';
import type { BookingPrices, AvailableWeekdays, ValidationError } from '~/common/types/booking';
import type { SearchFilters, WeekDays } from '~/common/types/search';
import type { SitterProfileSitterSettings, SitterUnavailableDates } from '~/common/types/sitter';
import type { ServiceOption } from '~/components/constants';

interface CalculateBookingPriceResponse {
  bookingPrices: BookingPrices | null;
  errors: ValidationError[] | null;
}

interface AvailableWeekdaysResponse {
  errors: ValidationError[] | null;
  weekdays: AvailableWeekdays | null;
}

interface OvernightTypePayload {
  endDate: string;
  petTypes: string;
  serviceTypeId: number;
  startDate: string;
}

interface DaytimeTypePayload {
  petTypes: string;
  scheduledDates: string;
  serviceTypeId: number;
}

interface RepeatWeeklyTypePayload {
  petTypes: string;
  scheduledDates: string;
  serviceTypeId: number;
  type: typeof BOOKING_TYPE_RWB;
}

interface AvailableWeekdaysPayload {
  petTypes: string;
  serviceTypeId: number;
  startDate: string;
  weekDays: string;
}

export const contactWidgetValidationSchema = yup.object({
  petTypes: petTypesValidationObject,
  chronology: chronologyValidationObject,
});

export const generateDateRangeByWeekDays = (startDate: string, weekDays: WeekDays): string => {
  const [sunday, ...rest] = Object.values(weekDays).map((item, key) => ({
    dayKey: key || 7,
    dayValue: item,
  }));
  const weekDaysFromMonday = [...rest, sunday];

  return weekDaysFromMonday
    .filter(({ dayValue }) => dayValue)
    .map(({ dayKey }) =>
      dayjs(startDate)
        .add(dayKey - 1, 'days')
        .format('YYYY-MM-DD')
    )
    .join(', ');
};

/**
 * Get from search filters (form values)
 * payload required to calculate booking prices
 */
export const getPayloadForCalculateBookingPrice = (
  values: SearchFilters
): OvernightTypePayload | DaytimeTypePayload | RepeatWeeklyTypePayload => {
  const serviceTypeId = SERVICE_TYPE_SLUGS_TO_SERVICE_TYPE_IDS[values.service.type];
  const petTypes = getPetTypesFromSearchFilters(values.petTypes);

  if (OVERNIGHT_SERVICE_TYPE_IDS.includes(serviceTypeId)) {
    const overnighTypetPayload: OvernightTypePayload = {
      endDate: values.chronology.endDate || '',
      petTypes,
      serviceTypeId,
      startDate: values.chronology.startDate || '',
    };

    return overnighTypetPayload;
  }

  if (
    values.bookingType === BOOKING_TYPE_RWB &&
    SERVICE_TYPES_SUPPORTING_RWB.includes(serviceTypeId)
  ) {
    const repeatWeeklyTypePayload: RepeatWeeklyTypePayload = {
      petTypes,
      serviceTypeId,
      type: values.bookingType,
      scheduledDates: generateDateRangeByWeekDays(
        values.chronology.rwbStartDate,
        values.chronology.weekDays
      ),
    };

    return repeatWeeklyTypePayload;
  }

  const daytimeTypePayload: DaytimeTypePayload = {
    petTypes,
    scheduledDates: values.chronology.scheduledDates?.toString() || '',
    serviceTypeId,
  };

  return daytimeTypePayload;
};

export const calculateBookingPrice = async (
  sitterId: number,
  payload: OvernightTypePayload | DaytimeTypePayload | RepeatWeeklyTypePayload
): Promise<CalculateBookingPriceResponse> => {
  let bookingPrices = null;
  let errors = null;

  await SitterApi.getSitterBookingPrice({
    sitterId,
    ...payload,
  })
    .then((response) => {
      bookingPrices = response.data;
    })
    .catch((error) => {
      const validationErrors =
        (error as AxiosError<{ errors?: ValidationError[] }>).response?.data?.errors || [];

      if (validationErrors.length) {
        errors = validationErrors;
      }
    });

  return {
    bookingPrices,
    errors,
  };
};

/**
 * Get from search filters (form values)
 * payload required to get sitter's availdable weekdays
 * for RWB bookings
 */
export const getPayloadForAvailableWeekdays = (values: SearchFilters): AvailableWeekdaysPayload => {
  const serviceTypeId = SERVICE_TYPE_SLUGS_TO_SERVICE_TYPE_IDS[values.service.type];
  const petTypes = getPetTypesFromSearchFilters(values.petTypes);

  const repeatWeeklyTypePayload: AvailableWeekdaysPayload = {
    startDate: values.chronology.rwbStartDate,
    petTypes,
    serviceTypeId,
    weekDays: [
      values.chronology.weekDays.sunday ? 1 : 0,
      values.chronology.weekDays.monday ? 1 : 0,
      values.chronology.weekDays.tuesday ? 1 : 0,
      values.chronology.weekDays.wednesday ? 1 : 0,
      values.chronology.weekDays.thursday ? 1 : 0,
      values.chronology.weekDays.friday ? 1 : 0,
      values.chronology.weekDays.saturday ? 1 : 0,
    ].join(','),
  };

  return repeatWeeklyTypePayload;
};

export const getAvailableWeekdays = async (
  sitterId: number,
  payload: AvailableWeekdaysPayload
): Promise<AvailableWeekdaysResponse> => {
  let weekdays = null;
  let errors = null;

  try {
    const availableWeekdays = await SitterApi.getSitterCalendarWeekdaysAvailability({
      sitterId,
      ...payload,
    });
    const { errors: availableWeekdaysError, data: weekdaysFromQuery } = availableWeekdays;

    weekdays = weekdaysFromQuery;

    // An array is retuned if values is empty
    // Need to handle if before continuing
    if (!Array.isArray(availableWeekdaysError)) {
      errors = availableWeekdaysError ? [availableWeekdaysError] : null;
    }
  } catch (error) {
    const validationErrors =
      (error as AxiosError<{ errors?: ValidationError[] }>).response?.data?.errors || [];

    if (validationErrors.length) {
      errors = validationErrors;
    }
  }

  return {
    weekdays,
    errors,
  };
};

/**
 * Based on error object from API response
 * returns Formik's field name
 * that should be updated with received error message
 */
export const getFieldName = (fieldNameFromApi: string): string => {
  if (fieldNameFromApi === 'serviceTypeId') {
    return 'service';
  }

  if (
    ['scheduledDates', 'startDate', 'endDate', 'weekDays', 'rwbStartDate'].includes(
      fieldNameFromApi
    )
  ) {
    return 'chronology';
  }

  return fieldNameFromApi;
};

export const getBookingTypeFromValues = (values: SearchFilters): string => {
  const serviceTypeId = SERVICE_TYPE_SLUGS_TO_SERVICE_TYPE_IDS[values.service.type];

  if (
    values.bookingType === BOOKING_TYPE_RWB &&
    SERVICE_TYPES_SUPPORTING_RWB.includes(serviceTypeId)
  ) {
    return BOOKING_TYPE_RWB;
  }

  return BOOKING_TYPE_MARKETPLACE;
};

export const filterProvidedServiceTypes = (
  serviceOptions: ServiceOption[],
  sitterSettings: SitterProfileSitterSettings
): ServiceOption[] =>
  serviceOptions.filter((option) =>
    sitterSettings.acceptedServiceTypes.includes(option.serviceTypeId)
  );

export const checkAvailability = async (
  sitterId: number,
  serviceTypeId: number,
  bookingType: string
): Promise<SitterUnavailableDates> => {
  try {
    const unavailableDates = await SitterApi.getSitterCalendarAvailabilty(
      sitterId,
      serviceTypeId,
      bookingType
    );

    return { unavailableDates };
  } catch (error) {
    return { unavailableDates: [] };
  }
};

/**
 * Get initial service type id based on search filters
 * or from sitter's settings in case of invalid service type
 */
export const getInitialServiceTypeId = (
  searchFilters: SearchFilters,
  sitterSettings: SitterProfileSitterSettings
): number => {
  const serviceTypeId = SERVICE_TYPE_SLUGS_TO_SERVICE_TYPE_IDS[searchFilters.service.type];

  if (serviceTypeId) {
    return serviceTypeId;
  }

  return sitterSettings.acceptedServiceTypes[0] || DEFAULT_SERVICE_TYPE_ID;
};
